import { useState, useEffect, useRef } from "react";
import styles from "../styles/OptimizedImage.module.css";
import { getCachedImageUrl, cacheImageUrl, extractStoragePathFromUrl } from "../utils/imageCache";

/**
 * OptimizedImage - A component that provides:
 * - Lazy loading (only loads when in viewport)
 * - Loading placeholder
 * - Error handling with fallback
 * - Optional image optimization for Firebase Storage URLs
 */
const OptimizedImage = ({
    src,
    alt = "",
    className = "",
    style = {},
    placeholder = null, // Optional placeholder image URL
    onLoad,
    onError,
    lazy = true, // Enable lazy loading by default
    width,
    height,
    objectFit = "cover",
}) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isInView, setIsInView] = useState(!lazy);
    const imgRef = useRef(null);
    const observerRef = useRef(null);
    const timeoutRef = useRef(null);

    // Optimize Firebase Storage URLs and check cache
    const optimizeImageUrl = (url) => {
        if (!url) return null;
        
        // Check if this is a Firebase Storage URL and if we have it cached
        try {
            const storagePath = extractStoragePathFromUrl(url);
            if (storagePath) {
                const cachedUrl = getCachedImageUrl(storagePath);
                if (cachedUrl) {
                    return cachedUrl;
                }
                // Cache the URL for future use
                cacheImageUrl(storagePath, url);
            }
        } catch (error) {
            console.warn('Error optimizing image URL:', error);
        }
        
        return url;
    };

    // Intersection Observer for lazy loading
    useEffect(() => {
        if (!lazy || isInView) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        if (observerRef.current) {
                            observerRef.current.disconnect();
                            observerRef.current = null;
                        }
                    }
                });
            },
            {
                rootMargin: "50px", // Start loading 50px before image enters viewport
                threshold: 0.01,
            }
        );

        if (imgRef.current) {
            observerRef.current = observer;
            observer.observe(imgRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [lazy, isInView]);

    // Load image when in view - simplified to avoid double loading
    useEffect(() => {
        if (!isInView) return;

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // If no src provided, show error/placeholder immediately
        if (!src || src.trim() === '') {
            setIsLoading(false);
            setHasError(true);
            setImageSrc(null);
            return;
        }

        setIsLoading(true);
        setHasError(false);

        const optimizedUrl = optimizeImageUrl(src);
        setImageSrc(optimizedUrl);

        // Set a timeout to show placeholder if image takes too long to load
        timeoutRef.current = setTimeout(() => {
            setIsLoading(false);
            setHasError(true);
            timeoutRef.current = null;
        }, 10000); // 10 second timeout

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isInView, src]);

    const containerStyle = {
        ...style,
        width: width || style.width,
        height: height || style.height,
        position: "relative",
        overflow: "hidden",
    };

    return (
        <div
            ref={imgRef}
            className={`${styles.imageContainer} ${className}`}
            style={containerStyle}
        >
            {/* Loading placeholder */}
            {isLoading && (
                <div className={styles.loadingPlaceholder}>
                    <div className={styles.loadingSpinner}></div>
                </div>
            )}

            {/* Error placeholder - use custom placeholder image if provided, otherwise show error icon */}
            {hasError && (
                placeholder ? (
                    <img
                        src={placeholder}
                        alt={alt || "Placeholder"}
                        className={styles.optimizedImage}
                        style={{ objectFit }}
                    />
                ) : (
                    <div className={styles.errorPlaceholder}>
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <span>Image unavailable</span>
                    </div>
                )
            )}

            {/* Actual image */}
            {imageSrc && !hasError && (
                <img
                    src={imageSrc}
                    alt={alt}
                    className={styles.optimizedImage}
                    style={{
                        opacity: isLoading ? 0 : 1,
                        transition: "opacity 0.3s ease-in-out",
                        objectFit,
                    }}
                    loading={lazy ? "lazy" : "eager"}
                    decoding="async"
                    fetchPriority={lazy ? "low" : "high"}
                    onLoad={() => {
                        // Clear timeout if image loads successfully
                        if (timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                            timeoutRef.current = null;
                        }
                        setIsLoading(false);
                        setHasError(false);
                        if (onLoad) onLoad();
                    }}
                    onError={() => {
                        // Clear timeout if image errors
                        if (timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                            timeoutRef.current = null;
                        }
                        setIsLoading(false);
                        setHasError(true);
                        setImageSrc(null); // Clear the failed image src
                        if (onError) onError();
                    }}
                />
            )}

            {/* Custom placeholder if provided */}
            {placeholder && isLoading && !hasError && (
                <img
                    src={placeholder}
                    alt=""
                    className={styles.placeholderImage}
                    style={{ opacity: 0.5 }}
                />
            )}
        </div>
    );
};

export default OptimizedImage;

